import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronRight,
  Sparkles,
  LayoutGrid,
  FileText,
  Wand2,
  Lightbulb,
  ExternalLink,
  Check,
} from 'lucide-react';
import Link from '@/components/LocaleLink';
interface OnboardingTourProps {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onComplete: () => void;
  goToStep: (step: number) => void;
}

interface TabConfig {
  id: string;
  icon: React.ReactNode;
  color: string;
  link?: string;
}

const TAB_CONFIGS: TabConfig[] = [
  {
    id: 'overview',
    icon: <Sparkles size={18} />,
    color: 'text-iris-400',
  },
  {
    id: 'hubs',
    icon: <LayoutGrid size={18} />,
    color: 'text-blue-400',
    link: '/features',
  },
  {
    id: 'scoops',
    icon: <FileText size={18} />,
    color: 'text-emerald-400',
  },
  {
    id: 'ai',
    icon: <Wand2 size={18} />,
    color: 'text-amber-400',
    link: '/features',
  },
  {
    id: 'tips',
    icon: <Lightbulb size={18} />,
    color: 'text-pink-400',
  },
];

export default function OnboardingTour({
  isActive,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onComplete,
  goToStep,
}: OnboardingTourProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (currentStep === totalSteps - 1) {
          onComplete();
        } else {
          onNext();
        }
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        onPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, currentStep, totalSteps, onNext, onPrev, onSkip, onComplete]);

  if (!isActive) return null;

  const currentTab = TAB_CONFIGS[currentStep] || TAB_CONFIGS[0];
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-100 bg-black/70 backdrop-blur-xs transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onSkip}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-101 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative w-full max-w-2xl bg-[#0c0c14] border border-white/10 rounded-2xl shadow-2xl pointer-events-auto transition-all duration-300 overflow-hidden ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/6 flex-wrap gap-y-2">
            <h2 className="text-lg font-semibold text-white">
              {t('onboarding.welcomeTitle')}
            </h2>
            <button
              onClick={onSkip}
              className="p-2 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex min-h-[400px]">
            {/* Sidebar */}
            <div className="w-48 shrink-0 border-r border-white/6 py-2">
              {TAB_CONFIGS.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => goToStep(index)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                    currentStep === index
                      ? 'bg-white/6 border-r-2 border-iris-500'
                      : 'hover:bg-white/3'
                  }`}
                >
                  <span className={currentStep === index ? tab.color : 'text-[#9a9aac]'}>
                    {tab.icon}
                  </span>
                  <span
                    className={`text-sm ${
                      currentStep === index ? 'text-white font-medium' : 'text-[#8e8e9e]'
                    }`}
                  >
                    {t(`tabs.${tab.id}`)}
                  </span>
                  {index < currentStep && (
                    <Check size={14} className="ml-auto text-emerald-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="space-y-4">
                <div className={`inline-flex p-2 rounded-xl bg-white/3 ${currentTab.color}`}>
                  {currentTab.icon}
                </div>

                <h3 className="text-xl font-semibold text-white">
                  {t(`content.${currentTab.id}.title`)}
                </h3>

                <p className="text-[#a0a0b0] leading-relaxed">
                  {t(`content.${currentTab.id}.description`)}
                </p>

                {/* Bullet points */}
                <ul className="space-y-2 pt-2">
                  {[1, 2, 3].map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#8e8e9e]">
                      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${currentTab.color.replace('text-', 'bg-')}`} />
                      {t(`content.${currentTab.id}.bullet${i}`)}
                    </li>
                  ))}
                </ul>

                {/* Learn more link */}
                {currentTab.link && (
                  <Link to={currentTab.link}
                    className="inline-flex items-center gap-1.5 text-sm text-iris-400 hover:text-iris-300 transition-colors mt-4"
                  >
                    {t('onboarding.learnMore')}
                    <ExternalLink size={14} />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/6 flex-wrap gap-y-2">
            <button
              onClick={onSkip}
              className="px-4 py-2 text-sm text-[#9a9aac] hover:text-white transition-all"
            >
              {t('onboarding.skip')}
            </button>

            <div className="flex items-center gap-3">
              {/* Progress text */}
              <span className="text-xs text-[#9a9aac]">
                {currentStep + 1} / {totalSteps}
              </span>

              <button
                onClick={isLastStep ? onComplete : onNext}
                className="flex items-center gap-1 px-5 py-2.5 rounded-xl text-sm font-semibold text-[#0C0C12] bg-iris-600 hover:bg-iris-500 transition-all"
              >
                {isLastStep ? t('onboarding.getStarted') : t('onboarding.next')}
                {!isLastStep && <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
